import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import GeneralRegistrationVM from '../general-registration/general-registration.viewmodel';
import { alertServerError, showNotification } from '../../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { RegistrationConfigurationDAO } from '../../../../../../../api/implementations/registration-configuration.dao';
import { getFormattedUrl } from '../../../../../../../infrastructure/utils/common.utils';
import TrackingServiceProvider from '../../../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../../../../tracking-service/tracking-values';
import { LinkTargetValue } from '../../../../../../../constants/common.constants';
export default class GeneralRegistrationFormVM {

  @mobx.observable
  public generalRegistrationVM: GeneralRegistrationVM;

  @mobx.observable
  public isSaving = false;

  @mobx.observable
  public isFormApplied: boolean;

  @mobx.observable
  public isLoading: boolean;

  constructor(
    private readonly accountId: number,
    private readonly eventId: number,
    private readonly subdomain: string,
    private readonly registrationEnabled: boolean,
    private readonly registrationApprovalEnabled: boolean,
    private readonly urlParameterRegistrationEnabled: boolean,
    private toggleAttendeeRegistrationWebsiteForm: (registrationEnabled: boolean) => void,
    private showFormRegistrationFormPopup: () => void,
    private readonly registrationAsLandingPage: boolean,
    private toggleRegistrationAsLandingWebsiteForm: (registrationAsLandingPage: boolean) => void,
    private closeConfigureRegistrationForm: () => void,
    @IoC.Inject private readonly registrationConfigurationDAO: RegistrationConfigurationDAO = IoC.Container.get(RegistrationConfigurationDAO),
    @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
  ) {
    this.isLoading = true;
    this.accountId = accountId;
    this.eventId = eventId;

    this.generalRegistrationVM = new GeneralRegistrationVM(
        this.registrationEnabled,
        this.getIsFormApplied,
        this.registrationAsLandingPage,
        this.registrationApprovalEnabled,
        this.urlParameterRegistrationEnabled,
    );
    this.generalRegistrationVM.showFormRegistrationFormPopup = this.showFormRegistrationFormPopup;
    this.generalRegistrationVM.generalRegistrationFormOptions = {
      closeConfigureRegistrationForm: this.closeConfigureRegistrationForm,
      onApply: this.applyRegistrationForm,
      getIsSaving: this.getIsSaving,
    };

    this.isFormApplied = false;
  }

  @mobx.action
  public async initialize() {
    await this.getGeneralRegistrationConfiguration();
  }

  public getGeneralRegistrationConfiguration = async (): Promise<void> => {
    try {
      const result = await this.registrationConfigurationDAO.getRegistrationConfiguration(this.eventId, this.accountId);
      mobx.runInAction(() => {
        this.generalRegistrationVM.onFetchGeneralRegistrationSuccess(result);
      });
    } catch (e) {
      GeneralRegistrationFormVM.handleApiException(e);
    } finally {
      mobx.runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  public getIsSaving = (): boolean => {
    return this.isSaving;
  };

  public getIsFormApplied = (): boolean => {
    return this.isFormApplied;
  };

  public static handleApiException(e: Error, title: string = 'Error'): void {
    if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
  }

  @mobx.action
  public applyRegistrationForm = async (): Promise<boolean> => {
    try {
      mobx.runInAction(() => {
        this.isFormApplied = true;
        this.isSaving = true;
      });

      if (!this.generalRegistrationVM.checkValidity()) return false;

      const registrationConfiguration = {
        ...this.generalRegistrationVM.getGeneralRegistrationFields()
      };
      const response = await this.registrationConfigurationDAO.updateRegistrationConfiguration(this.accountId, this.eventId, registrationConfiguration);

      showNotification('Form updated');
      mobx.runInAction(() => {
        this.toggleAttendeeRegistrationWebsiteForm(response.enabled);
      });

      mobx.runInAction(() => {
        this.toggleRegistrationAsLandingWebsiteForm(response.asLandingPage);
      });

      await this.getGeneralRegistrationConfiguration();

      this.trackingService.get().track(TRACKING_EVENTS.REGISTRATION_CONFIGURATION,
        { ...registrationConfiguration.registrationFieldsChanges }
      );

      this.generalRegistrationVM.setInitials();

      return true;
    }
    catch (e) {
      GeneralRegistrationFormVM.handleApiException(e);
      return false;
    } finally {
      mobx.runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  @mobx.action
  public openRegistrationFormPreview = (): void => {
    const link = getFormattedUrl(this.subdomain) + 'registration/';
    window.open(link, LinkTargetValue.BLANK);
  };
}